import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Badge,
  Box,
  Button,
  Flex,
  HStack,
  Icon,
  Input,
  InputGroup,
  InputLeftElement,
  Spinner,
  Text,
  VStack,
  useColorModeValue,
  useToast,
} from "@chakra-ui/react";
import { Settings2, Save, Search, CheckCircle2, Circle } from "lucide-react";
import { FieldKeySelector } from "../missions/components/FieldKeySelector";
import type { AvailableKey } from "../missions/components/FieldKeySelector";
import { useApiRequest } from "../../../hooks/useApiRequest.ts";
import { settings } from "../../../shared/settings.ts";

const BASE_URL = settings.environment.VITE_STARTER_PACK_BASE_URL;

interface FieldMapping {
  field_name: string;
  field_key: string | null;
}

const SEMANTIC_FIELDS: { name: string; label: string; description: string }[] = [
  { name: "nome",               label: "Nome do candidato",    description: "Nome completo exibido nos tickets" },
  { name: "cpf",                label: "CPF",                  description: "CPF do candidato" },
  { name: "cargo",              label: "Cargo",                description: "Cargo / posição" },
  { name: "setor",              label: "Setor / Departamento", description: "Time ou área do candidato" },
  { name: "sistemaoperacional", label: "Sistema Operacional",  description: "Windows, macOS, Linux..." },
  { name: "cep",                label: "CEP",                  description: "Localidade: BH / SP / Remoto" },
  { name: "datainicio",         label: "Data de Início",       description: "Data de início do candidato" },
  { name: "emailsugerido",      label: "E-mail Sugerido",       description: "E-mail sugerido para o novo colaborador (Atlassian)" },
  { name: "fotobiometria",      label: "Foto de Biometria",     description: "Foto do candidato para cadastro de biometria no ticket de infra" },
  { name: "razao_social",        label: "Razão Social (PJ)",       description: "Razão social da empresa do candidato PJ" },
  { name: "cnpj",                label: "CNPJ (PJ)",              description: "CNPJ da empresa do candidato PJ" },
  { name: "email_pessoal",       label: "E-mail Pessoal (PJ)",    description: "E-mail pessoal do candidato PJ" },
  { name: "banco",               label: "Banco (PJ)",            description: "Banco para dados bancários do candidato PJ" },
  { name: "agencia",             label: "Agência (PJ)",          description: "Agência bancária do candidato PJ" },
  { name: "conta",               label: "Conta (PJ)",            description: "Conta bancária do candidato PJ" },
  { name: "titular_conta_nome",  label: "Titular da Conta (PJ)", description: "Nome do titular da conta bancária (PJ)" },
  { name: "titular_conta_documento", label: "CPF/CNPJ do Titular (PJ)", description: "CPF ou CNPJ do titular da conta (PJ)" },
];

// ---------- Main Page ----------

const TicketMappingsPage = ({ standalone = true }: { standalone?: boolean }) => {
  const [mappings, setMappings]           = useState<Record<string, string | null>>({});
  const [availableKeys, setAvailableKeys] = useState<AvailableKey[]>([]);
  const [isSaving, setIsSaving]           = useState(false);
  const [search, setSearch]               = useState("");

  const { request: fetchMappings, isLoading: loadingMappings } = useApiRequest();
  const { request: fetchMissions, isLoading: loadingMissions } = useApiRequest();
  const { request: saveReq } = useApiRequest();
  const toast = useToast();

  const bg               = useColorModeValue("white", "#132035");
  const borderColor      = useColorModeValue("rgba(0,0,0,0.08)", "rgba(255,255,255,0.08)");
  const labelColor       = useColorModeValue("#434347", "#e8edf2");
  const descColor        = useColorModeValue("#696a71", "#8fa3b8");
  const rowHoverBg       = useColorModeValue("rgba(0,158,251,0.04)", "#1a2d47");
  const configuredColor  = useColorModeValue("#00a46e", "#48bb78");
  const unconfiguredColor = useColorModeValue("rgba(0,0,0,0.2)", "rgba(255,255,255,0.2)");
  const mappedKeyBg      = useColorModeValue("rgba(0,158,251,0.10)", "rgba(0,158,251,0.20)");
  const mappedKeyColor   = "#009efb";
  const iconBoxBg        = useColorModeValue("rgba(0,158,251,0.10)", "rgba(0,158,251,0.15)");
  const searchBg         = useColorModeValue("white", "rgba(255,255,255,0.06)");
  const searchBorder     = useColorModeValue("rgba(0,0,0,0.15)", "rgba(255,255,255,0.15)");
  const searchPlaceholder = useColorModeValue("#84858c", "#3d5a75");
  const cardShadow       = useColorModeValue("0px 4px 8px 0px rgba(14, 61, 93, 0.08)", "0px 4px 16px rgba(0,0,0,0.4)");

  const load = useCallback(async () => {
    const [mappingsData, missionsData] = await Promise.all([
      fetchMappings({
        url: `${BASE_URL}/api/v1/admin/ticket-mappings`,
        method: "GET",
        skipToast: true,
      }).catch(() => []),
      fetchMissions({
        url: `${BASE_URL}/api/v1/admin/missions`,
        method: "GET",
        skipToast: true,
      }).catch(() => []),
    ]);

    if (Array.isArray(mappingsData)) {
      const map: Record<string, string | null> = {};
      (mappingsData as FieldMapping[]).forEach(r => { map[r.field_name] = r.field_key ?? null; });
      setMappings(map);
    }

    if (Array.isArray(missionsData)) {
      const keys: AvailableKey[] = [];
      const seen = new Set<string>();
      (missionsData as any[]).forEach(mission => {
        // Exclui campos de visualização (reference_source definido)
        (mission.fields ?? []).filter((f: any) => !f.reference_source).forEach((f: any) => {
          if (!seen.has(f.key)) {
            seen.add(f.key);
            keys.push({ key: f.key, label: f.label, mission_title: mission.title });
          }
        });
      });
      setAvailableKeys(keys);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSave = async () => {
    setIsSaving(true);
    try {
      await saveReq({
        url: `${BASE_URL}/api/v1/admin/ticket-mappings`,
        method: "PUT",
        payload: {
          mappings: SEMANTIC_FIELDS.map(f => ({
            field_name: f.name,
            field_key: mappings[f.name] ?? null,
          })),
        },
        skipToast: true,
      });
      toast({ title: "Mapeamentos salvos com sucesso.", status: "success", duration: 4000, isClosable: true });
    } catch {
      // handled
    } finally {
      setIsSaving(false);
    }
  };

  const isLoading = loadingMappings || loadingMissions;

  const configuredCount = SEMANTIC_FIELDS.filter(f => mappings[f.name]).length;

  const keysByMission = useMemo(() => {
    const groups: Record<string, AvailableKey[]> = {};
    availableKeys.forEach(k => {
      if (!groups[k.mission_title]) groups[k.mission_title] = [];
      groups[k.mission_title].push(k);
    });
    return groups;
  }, [availableKeys]);

  const filteredFields = useMemo(() => {
    const q = search.toLowerCase().trim();
    if (!q) return SEMANTIC_FIELDS;
    return SEMANTIC_FIELDS.filter(f =>
      f.label.toLowerCase().includes(q) || f.description.toLowerCase().includes(q)
    );
  }, [search]);

  return (
    <Box maxW="780px" mx="auto" py={standalone ? 8 : 0} px={standalone ? 4 : 0}>
      {standalone && (
        <HStack spacing={3} mb={6}>
          <Flex
            align="center" justify="center"
            w="40px" h="40px" borderRadius="10px"
            bg={iconBoxBg}
            border="1px solid rgba(0,158,251,0.2)"
            flexShrink={0}
          >
            <Icon as={Settings2} boxSize={5} color="#009efb" />
          </Flex>
          <Box>
            <Text fontSize="xl" fontWeight="bold" color={labelColor}>Mapeamento de Campos</Text>
            <Text fontSize="sm" color={descColor}>
              Configure qual key de campo corresponde a cada informação usada nos tickets.
            </Text>
          </Box>
        </HStack>
      )}

      {/* Summary + search */}
      <Flex align="center" justify="space-between" mb={4} gap={3} wrap="wrap">
        <HStack spacing={2}>
          <Text fontSize="sm" fontWeight="medium" color={labelColor}>
            {configuredCount} de {SEMANTIC_FIELDS.length} campos configurados
          </Text>
          <Badge
            borderRadius="50px"
            fontSize="11px"
            fontWeight={600}
            px={2.5}
            py={0.5}
            letterSpacing="0.5px"
            bg={
              configuredCount === SEMANTIC_FIELDS.length ? "rgba(0,164,110,0.15)"
              : configuredCount > 0 ? "rgba(237,119,19,0.15)"
              : "rgba(0,0,0,0.06)"
            }
            color={
              configuredCount === SEMANTIC_FIELDS.length ? "#00a46e"
              : configuredCount > 0 ? "#ed7713"
              : "#84858c"
            }
            border="1px solid"
            borderColor={
              configuredCount === SEMANTIC_FIELDS.length ? "rgba(0,164,110,0.3)"
              : configuredCount > 0 ? "rgba(237,119,19,0.3)"
              : "rgba(0,0,0,0.12)"
            }
          >
            {configuredCount === SEMANTIC_FIELDS.length ? "Completo" : configuredCount > 0 ? "Parcial" : "Pendente"}
          </Badge>
        </HStack>

        <InputGroup maxW="220px" size="sm">
          <InputLeftElement pointerEvents="none">
            <Icon as={Search} boxSize={3.5} color={descColor} />
          </InputLeftElement>
          <Input
            placeholder="Filtrar campos..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            borderRadius="50px"
            bg={searchBg}
            borderColor={searchBorder}
            color={labelColor}
            fontSize="13px"
            _focus={{ borderColor: "#009efb", boxShadow: "0 0 0 2px rgba(0,158,251,0.15)" }}
            _placeholder={{ color: searchPlaceholder }}
          />
        </InputGroup>
      </Flex>

      <Box
        borderWidth="1px"
        borderColor={borderColor}
        borderRadius="12px"
        bg={bg}
        boxShadow={cardShadow}
        overflow="hidden"
      >
        {isLoading ? (
          <Flex justify="center" align="center" py={16} gap={3}>
            <Spinner size="sm" color="#009efb" />
            <Text fontSize="sm" color={descColor}>Carregando campos...</Text>
          </Flex>
        ) : filteredFields.length === 0 ? (
          <Flex justify="center" py={12}>
            <Text fontSize="sm" color={descColor}>Nenhum campo encontrado.</Text>
          </Flex>
        ) : (
          <VStack spacing={0} align="stretch">
            {filteredFields.map((f, i) => {
              const value = mappings[f.name] ?? "";
              const isConfigured = Boolean(value);
              const mappedKey = availableKeys.find(k => k.key === value);

              return (
                <Box
                  key={f.name}
                  borderBottomWidth={i < filteredFields.length - 1 ? "1px" : 0}
                  borderColor={borderColor}
                  _hover={{ bg: rowHoverBg }}
                  transition="background 0.15s"
                >
                  <Flex align="flex-start" gap={4} px={5} py={4}>
                    {/* Status icon */}
                    <Flex pt="3px" flexShrink={0}>
                      <Icon
                        as={isConfigured ? CheckCircle2 : Circle}
                        boxSize={4}
                        color={isConfigured ? configuredColor : unconfiguredColor}
                      />
                    </Flex>

                    {/* Label + description + mapped badge */}
                    <Box flex={1} minW={0}>
                      <Text fontSize="sm" fontWeight="600" color={labelColor} lineHeight="1.4">
                        {f.label}
                      </Text>
                      <Text fontSize="xs" color={descColor} mt={0.5}>
                        {f.description}
                      </Text>
                      {isConfigured && mappedKey && (
                        <HStack mt={1.5} spacing={1.5}>
                          <Badge
                            bg={mappedKeyBg}
                            color={mappedKeyColor}
                            fontFamily="mono"
                            fontSize="10px"
                            fontWeight="700"
                            letterSpacing="0.8px"
                            textTransform="uppercase"
                            px={2}
                            py={0.5}
                            borderRadius="4px"
                            border="1px solid rgba(0,158,251,0.3)"
                          >
                            {mappedKey.key}
                          </Badge>
                          <Text fontSize="xs" color={descColor}>·</Text>
                          <Text fontSize="xs" color={descColor}>{mappedKey.label}</Text>
                        </HStack>
                      )}
                    </Box>

                    {/* Custom dropdown */}
                    <Box w="230px" flexShrink={0}>
                      <FieldKeySelector
                        value={value}
                        keysByMission={keysByMission}
                        onChange={val =>
                          setMappings(prev => ({ ...prev, [f.name]: val }))
                        }
                      />
                    </Box>
                  </Flex>
                </Box>
              );
            })}
          </VStack>
        )}
      </Box>

      <Flex justify="flex-end" mt={4}>
        <Button
          bg="#009efb"
          color="white"
          _hover={{ bg: "#007bd4" }}
          borderRadius="50px"
          leftIcon={<Icon as={Save} boxSize={4} />}
          isLoading={isSaving}
          loadingText="Salvando..."
          onClick={handleSave}
          isDisabled={isLoading}
          h="40px"
          px={6}
          fontSize="14px"
          fontWeight={500}
        >
          Salvar mapeamentos
        </Button>
      </Flex>
    </Box>
  );
};

export default TicketMappingsPage;
